import { Trans, t } from "@lingui/macro";
import React from "react";
import cx from "classnames";
import EventHeader from "pages/Event/components/EventHeader";
import EventTierCard from "pages/Event/components/EventTierCard";
import BuyPlpFromCard from "pages/Event/components/BuyPlpFormCard";
import EventOverviewCard from "pages/Event/components/EventOverviewCard";
import { EventCountdown } from "pages/Event/components/EventCountdown";
import { GradientContainer } from "components/GradientContainer";
import { EventDate } from "pages/Event/components/EventDate";
import { useEventGeneralInfoQuery } from "pages/Event/hooks/blockchain/useEventGeneralInfoQuery";
import Loader from "components/Common/Loader";
import { BigNumber } from "@ethersproject/bignumber";
import { formatUnitsPlp } from "pages/Event/utils/formatUnitsPlp";
import { useEventUserInfoQuery } from "pages/Event/hooks/blockchain/useEventUserInfoQuery";
import { formatAmount } from "../../lib/numbers";

const EVENT_IDS = {
  LAUNCH_EVENT: "LAUNCH_EVENT",
  TRADING_EVENT: "TRADING_EVENT",
} as const;

type EventId = keyof typeof EVENT_IDS;

type EventsData = {
  [key in EventId]: {
    label: string;
    description?: string;
  };
};

export type TierDetails = {
  tier: BigNumber;
  rewardPerToken: BigNumber;
  estimatedApr: BigNumber;
  totalRewards: BigNumber;
  startAmount: BigNumber;
  endAmount: BigNumber;
};

type EventProps = {
  connectWallet: () => {};
};

const Event = (props: EventProps) => {
  const eventGeneralInfoQuery = useEventGeneralInfoQuery();
  const eventUserInfoQuery = useEventUserInfoQuery();

  if (!eventGeneralInfoQuery.data) {
    return (
      <div className="h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <EventLoaded
      generalInfo={eventGeneralInfoQuery.data}
      userInfo={eventUserInfoQuery.data}
      connectWallet={props.connectWallet}
    />
  );
};

type EventLoadedProps = {
  generalInfo: Exclude<ReturnType<typeof useEventGeneralInfoQuery>["data"], undefined>;
  userInfo?: ReturnType<typeof useEventUserInfoQuery>["data"];
  connectWallet: () => {};
};

const eventEndTimestamp = new Date("2023-09-12T00:00:00Z").valueOf(); // 12 september 2023

const EventLoaded = ({ generalInfo, userInfo, connectWallet }: EventLoadedProps) => {
  const [selectedEventId, setSelectedEventId] = React.useState<EventId>("LAUNCH_EVENT");
  const eventsData = React.useMemo(() => {
    return {
      LAUNCH_EVENT: {
        label: t`Launch Event`,
        description: t`Provide liquidity and earn high APR on PALM and Trading fees in USDT.`,
      },
      TRADING_EVENT: {
        label: t`Trading Event`,
      },
    } satisfies EventsData;
  }, []);

  const howToSteps = React.useMemo(() => {
    return [
      t`Add Liquidity with USDT (Mint&nbsp;PLP)`,
      t`Earn high APR on&nbsp;PALM and other Rewards`,
      t`Claim your&nbsp;PALM Rewards daily after 30&nbsp;days`,
    ];
  }, []);

  const displayedEventIds = [
    "LAUNCH_EVENT",
    // Use for the trading event
    // 'TRADING_EVENT',
  ] satisfies EventId[];

  const tiersDetails = React.useMemo(() => {
    return generalInfo.tiersInfo.map((el) => {
      const capacity = el.upper.sub(el.lower);
      const totalRewards = el.rewardPerToken.mul(parseFloat(formatUnitsPlp(capacity)));
      const estimatedApr = totalRewards.div(el.allocatedPlp).mul(100);

      return {
        tier: el.tier,
        rewardPerToken: el.rewardPerToken,
        estimatedApr: estimatedApr,
        totalRewards: totalRewards,
        startAmount: el.lower,
        endAmount: el.upper,
      } satisfies TierDetails;
    });
  }, [generalInfo.tiersInfo]);

  const tier = generalInfo.currentTier;
  const tierDetails = tiersDetails[tier.toNumber()];
  const currentAmount = generalInfo.totalPurchased;

  return (
    <div className="tailwind flex items-stretch h-full isolate">
      {displayedEventIds.length >= 2 && (
        <aside className="w-[26rem] absolute border-r h-full p-[2rem]">
          <nav>
            <div className="text-[1.4rem] leading-[2.4rem] text-inactive mb-4">
              <Trans>Active Events</Trans>
            </div>

            <ul className="flex flex-col items-stretch gap-2">
              {displayedEventIds.map((id) => (
                <li className="relative" key={id}>
                  <button
                    className={cx([
                      "h-[4.2rem] rounded-sm hover:bg-white hover:bg-opacity-[7%] flex items-center px-4 w-full",
                      {
                        "bg-white bg-opacity-[7%]": id === selectedEventId,
                      },
                    ])}
                    onClick={() => setSelectedEventId(id)}
                  >
                    {eventsData[id].label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
      )}

      <div
        className={cx([
          "flex-1 px-8 py-[4rem] flex @container/page-content",
          {
            "ml-[26rem]": displayedEventIds.length >= 2,
          },
        ])}
      >
        <div className="justify-center flex-1 mx-auto max-w-[114rem] flex flex-col items-stretch">
          <EventHeader />
          {selectedEventId === "LAUNCH_EVENT" && (
            <>
              <header>
                <div className="flex flex-col @[100rem]/page-content:flex-row justify-between gap-8 @[100rem]/page-content:gap-4 items-start">
                  <div className="flex flex-col gap-4">
                    <h1 className="text-[2.4rem] leading-[1.4] font-medium">{eventsData[selectedEventId].label}</h1>

                    <p className="text-[1.4rem] leading-[1.5] text-inactive">
                      {eventsData[selectedEventId].description}
                    </p>
                  </div>

                  <div className="flex flex-col">
                    <p className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14">
                        <path
                          d="M 7 12.6 C 10.093 12.6 12.6 10.093 12.6 7 C 12.6 3.907 10.093 1.4 7 1.4 C 3.907 1.4 1.4 3.907 1.4 7 C 1.4 10.093 3.907 12.6 7 12.6 Z"
                          fill="rgb(108, 114, 132)"
                        ></path>
                        <path
                          d="M 7.525 3.5 C 7.525 3.21 7.29 2.975 7 2.975 C 6.71 2.975 6.475 3.21 6.475 3.5 L 6.475 7 C 6.475 7.29 6.71 7.525 7 7.525 L 9.8 7.525 C 10.09 7.525 10.325 7.29 10.325 7 C 10.325 6.71 10.09 6.475 9.8 6.475 L 7.525 6.475 Z"
                          fill="#0f1014"
                        ></path>
                      </svg>

                      <span className="text-[1.4rem] leading-[1.5] text-inactive">
                        <Trans>Event Ends in:</Trans>
                      </span>
                    </p>

                    <p className="text-[2.4rem] leading-[1.4] font-extrabold font-mazzard tabular-nums">
                      <EventCountdown
                        eventEndTimestamp={eventEndTimestamp}
                        stopped={generalInfo.stopped || generalInfo.eventEnded}
                      />
                    </p>

                    <p className="text-inactive text-[1.4rem] leading-[1.5]">
                      <EventDate eventEndTimestamp={eventEndTimestamp} />
                    </p>
                  </div>
                </div>
              </header>

              <div className="mt-12 grid @[75rem]/page-content:grid-cols-2 @[100rem]/page-content:grid-rows-1 gap-6 ">
                <div className="@[75rem]/page-content:col-span-2 @[100rem]/page-content:col-span-1 @[100rem]/page-content:row-span-2">
                  <BuyPlpFromCard
                    className="h-full"
                    currentTier={tierDetails}
                    tiersDetails={tiersDetails}
                    currentAmount={currentAmount}
                    connectWallet={connectWallet}
                    isNotActive={generalInfo.stopped || generalInfo.eventEnded}
                  />
                </div>

                <div className="p-10 bg-background-5-v2-only rounded-[2.5rem] border border-solid @container/about">
                  <header className="flex flex-col @[40rem]/about:flex-row justify-between gap-4 @[40rem]/about:gap-8 items-start @[40rem]/about:items-center relative">
                    <div className="text-[2.2rem] leading-[1.4] font-extrabold font-mazzard">
                      <Trans>About Event</Trans>
                    </div>

                    <a
                      href="https://docs.palmswap.org/ecosystem/launch-event"
                      className="@[40rem]/about:absolute @[40rem]/about:bottom-0 @[40rem]/about:right-0 py-4 px-8 bg-white/10 hover:bg-main transition rounded-full flex items-center gap-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                        <path
                          d="M 3.6 1.6 C 2.937 1.6 2.4 2.137 2.4 2.8 L 2.4 13.2 C 2.4 13.863 2.937 14.4 3.6 14.4 L 12.4 14.4 C 13.063 14.4 13.6 13.863 13.6 13.2 L 13.6 6.097 C 13.6 5.779 13.473 5.474 13.248 5.249 L 9.952 1.951 C 9.727 1.726 9.421 1.6 9.102 1.6 Z"
                          fill="hsl(0, 0%, 100%)"
                        ></path>
                        <path
                          d="M 5.4 10.8 C 5.069 10.8 4.8 11.069 4.8 11.4 C 4.8 11.731 5.069 12 5.4 12 L 10.6 12 C 10.931 12 11.2 11.731 11.2 11.4 C 11.2 11.069 10.931 10.8 10.6 10.8 Z"
                          fill="#2e2f34"
                        ></path>
                        <path
                          d="M 5.4 8.4 C 5.069 8.4 4.8 8.669 4.8 9 C 4.8 9.331 5.069 9.6 5.4 9.6 L 10.6 9.6 C 10.931 9.6 11.2 9.331 11.2 9 C 11.2 8.669 10.931 8.4 10.6 8.4 Z"
                          fill="#2e2f34"
                        ></path>
                      </svg>

                      <span className="text-[1.4rem] leading-[1.5]">
                        <Trans>View Documentation</Trans>
                      </span>
                    </a>
                  </header>

                  <p className="mt-6 text-inactive text-[1.4rem] leading-[1.5]">
                    <Trans>
                      PLP Liquidity Event is an opportunity to provide liquidity to the Palmswap ecosystem and earn
                      additional rewards in PALM. As a liquidity provider, you will receive 50% of the trading fees in
                      USDT, mint & burn fees, liquidation fees and a range of other rewards. The PLP tokens represent
                      your shares in the pool and can be redeemed for USDT at any time after the V2 Platform is launched
                      on July 19.
                    </Trans>
                  </p>
                </div>

                <GradientContainer className="@[100rem]/page-content:col-start-2 @container/how-to">
                  <div className="p-8">
                    {userInfo && userInfo.userInfo.purchased.gt(0) ? (
                      <div className="flex flex-column items-stretch">
                        <p className="text-[2.2rem] leading-[1.4] font-extrabold font-mazzard mb-12 ">
                          <Trans>Your Event Overview</Trans>
                        </p>

                        <div className="flex justify-start gap-14 flex-col  @[50rem]:flex-row">
                          <div>
                            <p className="mb-3">
                              <Trans>Total Commitment</Trans>
                            </p>
                            <p className="font-extrabold text-[2.4rem] font-mazzard mb-3">
                              ${formatAmount(userInfo.overviewInfo.totalCommitment, 18, 2, true)}
                            </p>
                            <p>
                              {userInfo.overviewInfo.numberOfCommitments} <Trans>Commitments</Trans>
                            </p>
                          </div>
                          <div>
                            <p className="mb-3">
                              <Trans>Total Rewards</Trans>
                            </p>
                            <p className="font-extrabold text-[2.4rem] font-mazzard mb-3">
                              {formatAmount(userInfo.overviewInfo.totalRewards, 18, 4, true)} PALM
                            </p>
                            <p>~ ${formatAmount(userInfo.overviewInfo.totalRewardsInUsd, 18, 2, true)}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-[2.2rem] leading-[1.4] font-extrabold font-mazzard">
                          <Trans>How to participate?</Trans>
                        </p>

                        <ul className="mt-20 grid grid-rows-3 grid-cols-none gap-4 @[50rem]:grid-rows-none @[50rem]:grid-cols-3 @[50rem]/how-to:gap-12">
                          {howToSteps.map((message, index) => {
                            return (
                              <li className="flex items-center gap-4" key={message}>
                                <div className="shrink-0 w-16 aspect-square rounded-full bg-white/20 flex items-center justify-center">
                                  <div className="text-white text-[1.8rem] leading-[1.5] font-bold">{index + 1}</div>
                                </div>

                                <div className="text-white text-[1.4rem] leading-[1.5]">
                                  <span dangerouslySetInnerHTML={{ __html: message }} />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </div>
                </GradientContainer>
              </div>

              <div className="mt-[5rem] @container/tier-cards">
                <h2 className="text-[2.4rem] leading-[1.4] font-medium">
                  <Trans>PLP Event</Trans>
                </h2>

                <ul className="mt-[2.4rem] grid grid-cols-1 @[66rem]/tier-cards:grid-cols-2 @[99rem]/tier-cards:grid-cols-3 gap-6">
                  {tiersDetails.map((details) => (
                    <li key={details.tier.toNumber()}>
                      <EventTierCard currentAmount={currentAmount} currentTier={tier} tierDetails={details} />
                    </li>
                  ))}
                </ul>

                <div className="mt-6 h-[40rem] relative">
                  <EventOverviewCard className="absolute h-full w-full" />
                </div>
              </div>
            </>
          )}
          {selectedEventId === "TRADING_EVENT" && (
            <>
              <header>{eventsData[selectedEventId].label}</header>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Event;
